// 平台活跃时长追踪 Hook：在用户「真的在用页面」时累计秒数，并定期写入 localStorage。
//
// 【整体流程（给第一次看代码的同学）】
// 1. App.vue 挂载时调用 usePlatformUsageTracker()，全站只启动一次。
// 2. 等用户登录且 getUserInfo 返回后，绑定 userId，并从 localStorage 读出历史秒数。
// 3. 用户在前台操作（点、滚、按键）→ 视为活跃；每 10 秒把新增秒数累加并 flush 到 localStorage。
// 4. 个人中心不直接连这个 Hook，而是读 localStorage（见 profile/index.vue）。
//
// 【为什么不一直计时？】
// - 切到其他标签页 / 最小化 → 暂停（不算使用平台）
// - 5 分钟没有任何操作 → 暂停（人可能走开了）
import { TOKEN_KEY } from "@/enums/CacheEnum";
import { useUserStore } from "@/store";
import {
  readPlatformUsageSeconds,
  resolvePlatformUsageUserKey,
  writePlatformUsageSeconds,
} from "@/utils/platform-usage-time";

// 闲置超过 5 分钟无操作，就不再累计（单位：毫秒）。
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

// 每隔 10 秒结算一次并写入 localStorage（单位：毫秒）。
const FLUSH_INTERVAL_MS = 10 * 1000;

// 鼠标移动、滚动等事件很频繁，同一秒内多次操作只记一次（节流 1 秒）。
const ACTIVE_MARK_THROTTLE_MS = 1000;

// 用这些 DOM 事件判断用户仍在使用页面；触发时会刷新最后活跃时间。
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
] as const;

/**
 * 启动平台使用时长统计（在 App.vue 根组件里调用一次即可）。
 *
 * 注意：
 * - 必须在 App 级调用，不要只在个人中心调用，否则离开个人中心就不计时了。
 * - 统计的是「当前浏览器 + 当前账号」的活跃时长，不是服务端全平台数据。
 */
export function usePlatformUsageTracker(): void {
  const userStore = useUserStore();

  // ---------- 内存里的状态（刷新页面前一直有效）----------

  // 当前账号已累计的总秒数（会先和 localStorage 合并，再在此基础上 +delta）。
  let accumulatedSeconds = 0;

  // 用户最后一次活跃时间戳；超过 IDLE_TIMEOUT 未更新则暂停累计。
  let lastActiveAt = Date.now();

  /**
   * 上一次 tick/flush 结算的时间点。
   * 每次 tick 用 (now - lastTickAt) 算出这之间过了多少秒，再累加到 accumulatedSeconds。
   */
  let lastTickAt = Date.now();

  // markActive 节流用：上次真正更新 lastActiveAt 的时间。
  let lastMarkAt = 0;

  /**
   * 当前正在统计的用户 key（resolvePlatformUsageUserKey 的结果）。
   * 为空表示还没识别到用户（例如 getUserInfo 还没返回），此时 flush 不会写盘。
   */
  let activeUserKey = "";

  /**
   * 是否满足「可以开始/继续统计」的前置条件。
   *
   * 必须同时满足：
   * 1. localStorage 里有登录 token（说明已登录）
   * 2. 能从 userStore 解析出 userId（说明 getUserInfo 已返回用户信息）
   *
   * @returns true = 可以统计；false = 只空转 tick，不累加秒数
   */
  function canTrack(): boolean {
    return (
      Boolean(localStorage.getItem(TOKEN_KEY)) &&
      Boolean(resolvePlatformUsageUserKey(userStore.user))
    );
  }

  /**
   * 当前这一瞬间，是否应该把流逝的时间算进「平台使用时长」。
   *
   * 在 canTrack 基础上还要：
   * - 浏览器标签页在前台（document.visibilityState === 'visible'）
   * - 距离上次用户操作不超过 5 分钟
   *
   * @param now 当前时间戳，默认 Date.now()
   */
  function shouldAccumulate(now = Date.now()): boolean {
    return (
      canTrack() &&
      document.visibilityState === "visible" &&
      now - lastActiveAt <= IDLE_TIMEOUT_MS
    );
  }

  /**
   * 记录「用户刚刚还在用页面」。
   *
   * @param force 为 true 时跳过节流（例如从后台切回前台时，应立即恢复活跃状态）
   */
  function markActive(force = false): void {
    const now = Date.now();
    if (!force && now - lastMarkAt < ACTIVE_MARK_THROTTLE_MS) return;
    lastMarkAt = now;
    lastActiveAt = now;
  }

  /**
   * 结算「自上次 tick 以来」经过的秒数，并累加到 accumulatedSeconds。
   *
   * 若 shouldAccumulate 为 false（后台、未登录、闲置），则只更新 lastTickAt，不加秒数。
   * 这样从后台切回来时，不会把「挂在后台的那段时间」误算进去。
   */
  function tick(): void {
    const now = Date.now();
    if (shouldAccumulate(now)) {
      const deltaSeconds = Math.floor((now - lastTickAt) / 1000);
      if (deltaSeconds > 0) accumulatedSeconds += deltaSeconds;
    }
    lastTickAt = now;
  }

  /**
   * 先 tick 结算内存里的增量，再写入 localStorage。
   *
   * 个人中心读的是 localStorage，所以必须定期 flush，否则页面上永远显示「不足1分」。
   */
  function flush(): void {
    tick();
    if (!activeUserKey) return;
    writePlatformUsageSeconds(activeUserKey, accumulatedSeconds);
  }

  /**
   * 当用户信息变化时（首次 getUserInfo、切换账号），绑定正确的 userKey 并合并历史数据。
   *
   * 典型场景：
   * - App 启动时 userStore 还是空的 → activeUserKey 为空，暂不计入磁盘
   * - 几百毫秒后 getUserInfo 返回 → watch 触发本函数 → 读出 platformUsageSeconds:xxx 继续累加
   *
   * 合并规则：accumulatedSeconds = max(localStorage 里的值, 内存里已算出的值)
   * 避免「用户信息晚到」时，把启动后已经 tick 过的秒数冲掉。
   */
  function syncActiveUserKey(): void {
    const nextUserKey = resolvePlatformUsageUserKey(userStore.user);
    if (nextUserKey === activeUserKey) return;

    // 切换账号前，先把旧账号的数据落盘
    if (activeUserKey) {
      tick();
      writePlatformUsageSeconds(activeUserKey, accumulatedSeconds);
    }

    const memorySeconds = accumulatedSeconds;
    activeUserKey = nextUserKey;

    // 登出或用户信息被清空
    if (!nextUserKey) {
      accumulatedSeconds = 0;
      lastActiveAt = Date.now();
      lastTickAt = Date.now();
      return;
    }

    const storedSeconds = readPlatformUsageSeconds(nextUserKey);
    accumulatedSeconds = Math.max(storedSeconds, memorySeconds);
    writePlatformUsageSeconds(activeUserKey, accumulatedSeconds);
    lastActiveAt = Date.now();
    lastTickAt = Date.now();
  }

  /**
   * 用户切换浏览器标签页时触发。
   * - 切走（hidden）：立刻 flush，防止关标签丢失未写入的秒数
   * - 切回（visible）：重置 lastTickAt，避免把在后台挂着的间隔算进去
   */
  function handleVisibilityChange(): void {
    if (document.visibilityState === "hidden") {
      flush();
      return;
    }
    lastTickAt = Date.now();
    markActive(true);
  }

  /** 页面即将卸载（刷新、关闭标签）时尽量再存一次 */
  function handlePageHide(): void {
    flush();
  }

  /** 用户交互事件的统一入口 → 更新 lastActiveAt */
  function handleActivity(): void {
    markActive();
  }

  /** 在 window 上注册 ACTIVITY_EVENTS 里列出的所有事件 */
  function bindActivityListeners(): void {
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });
  }

  /** 组件卸载时移除事件监听（与 bind 成对，必须传同一个 handleActivity 引用） */
  function unbindActivityListeners(): void {
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleActivity);
    });
  }

  /**
   * 监听 userStore 里和用户身份相关的字段。
   * immediate: true → Hook 一执行就先跑一遍 syncActiveUserKey（此时可能还是空用户）。
   */
  watch(
    () => userStore.user.userId,
    () => syncActiveUserKey(),
    { immediate: true }
  );

  // ---------- 启动定时器与全局事件 ----------

  const flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
  bindActivityListeners();
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", handlePageHide);

  /** App 卸载时（极少发生）清理资源并最后一次落盘 */
  onBeforeUnmount(() => {
    flush();
    if (flushTimer) clearInterval(flushTimer);
    unbindActivityListeners();
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pagehide", handlePageHide);
  });
}
